import CreationToolError from 'creation/src/creation-tools/types/error'
import CreationToolErrorFix from 'creation/src/creation-tools/types/error-fix'
import CreationToolOutputType from 'creation/src/creation-tools/types/output-type'
import CreationToolUserRestriction from 'creation/src/creation-tools/types/user-restriction'
import { cn } from '@/util/cn'

type Props = {
    className?: string
    error: CreationToolError
    userRestrictionAccess: CreationToolUserRestriction[]
    onGoBack: () => void
    onChangeOutputType: (outputType: CreationToolOutputType) => void
}

/**
 * Legacy structural reference: components/error-modal.tsx, ported
 * declaration by declaration against components/typography.ts
 * (`Title`/`Text`) and components/buttons.ts (`ToolButton`/`UtilityButton`)
 * for the pieces it composes. No measured geometry exists for this screen —
 * Task 13 only captured the welcome screen's success state, and Task 18
 * doesn't diff this one either (see task brief) — so every value below
 * comes from the legacy source, not a live measurement. `welcome/index.tsx`'s
 * own `Error = styled(ErrorModal)` override (`box-shadow: 0 6px 9px
 * rgba(0,0,0,0.5); width: 400px;`) is applied by the caller via `className`,
 * matching legacy's composition — see welcome-shell.tsx.
 *
 * `text-giphyWhite` on the outer div is not in any of the source styled-
 * components (`Content`/`Title`/`Text` all leave color unset) — legacy relies
 * on its site-wide `body { color: giphyWhite }` reset for that instead. That
 * reset IS supplied on the Next side too, just from a different place:
 * `<GlobalCss />` (`app/(site)/layout.tsx`) -> `packages/ui/src/css.ts`'s
 * `body { color: #fff }`, not `app/global.css`. This div's own explicit
 * `text-giphyWhite` is applied anyway rather than leaned on inherited body
 * colour, so the modal's colour doesn't depend on where in the tree it gets
 * mounted.
 *
 * Scoped down from legacy's `CreationToolsErrorModal`: this component has no
 * store/context access beyond `userRestrictionAccess` (threaded in by
 * welcome-shell.tsx, which reads it off the creation store) — see
 * error-modal.spec.tsx — and only the two recoveries welcome-shell.tsx ever
 * wires up: `onGoBack` and `onChangeOutputType`. Legacy's `sourceId`/
 * `onRetry` props and `stickerDisplayMode` override are dropped entirely:
 * the welcome screen's `<Error>` never passes them (always the 'large'
 * sticker, no retry handler, no step redirect target).
 *
 * Permission gating IS ported (legacy components/error-modal.tsx:78-98,
 * matched sequence-for-sequence):
 *
 * 1. `errorFixOutputType` is computed from the error's ORIGINAL fix and
 *    never revisited — legacy only nulls it when no `onChangeOutputType`
 *    handler is supplied, which welcome-shell.tsx always supplies here.
 *    That reproduces a real legacy quirk on purpose: an inaccessible fix
 *    with an output type (e.g. `NoTransparency` -> `UploadAsGif`, which
 *    requires `LoggedInOnly`) still fires `onChangeOutputType` when its
 *    button is clicked, even though steps 2-3 below downgrade the button's
 *    *label* to `SupportCenter`'s. Not a bug to fix — legacy's actual
 *    behavior, verified against error-modal.tsx line by line.
 * 2. `fix` falls back to `SupportCenter` when `userRestrictionAccess`
 *    doesn't cover the original fix's restriction (a logged-out user
 *    hitting `NoTransparency` sees the SupportCenter label/copy, not
 *    "Upload as GIF" — and a user without `access_dvr` sees it instead of
 *    "Continue to Super Cut" for `VideoTooLong`).
 * 3. `fix` falls back to `SupportCenter` again for a step-redirect or
 *    retry fix — `VideoNeedsTrim`'s fix (`TrimVideo`) needs internal
 *    navigation (`CreationToolStep.route` + the store's `basePath`) that
 *    isn't available as a prop here; `UploadFailed`'s fix (`RetryUpload`)
 *    needs an `onRetry` this component doesn't accept either. Same rule
 *    legacy itself applies whenever a fix's required handler isn't
 *    supplied (`fixStep` / `fix === RetryUpload && !onRetry`).
 */
const WelcomeErrorModal = ({ className, error, userRestrictionAccess, onGoBack, onChangeOutputType }: Props) => {
    const title = CreationToolError.title(error)
    const description = CreationToolError.description(error)
    const stickerId = CreationToolError.stickerId(error)

    const errorFix = CreationToolError.fix(error)
    // See point 1 above — computed from the original fix and never nulled,
    // since onChangeOutputType is always provided by welcome-shell.tsx.
    const errorFixOutputType = CreationToolErrorFix.outputType(errorFix)
    const canAccessErrorFix = CreationToolUserRestriction.hasAccess(
        CreationToolErrorFix.userRestriction(errorFix),
        userRestrictionAccess
    )

    let fix = errorFix
    if (!canAccessErrorFix) {
        fix = CreationToolErrorFix.SupportCenter
    }
    if (CreationToolErrorFix.step(fix) || fix === CreationToolErrorFix.RetryUpload) {
        fix = CreationToolErrorFix.SupportCenter
    }

    const fixOutputType = errorFixOutputType
    const fixUrl = CreationToolErrorFix.url(fix)
    const fixTitle = CreationToolErrorFix.title(fix)
    const fixDescription = CreationToolErrorFix.description(fix)

    const buttonClassName = cn(
        // ToolButton (base)
        'cursor-pointer rounded-[2px] select-none active:opacity-80',
        // UtilityButton
        'box-border h-9 px-[15px] text-center text-[15px] leading-9 font-bold text-white antialiased',
        // error-modal's local `Button`
        'mr-5 shrink-0 bg-giphyDarkGrey last:mr-0 last:w-full last:shrink last:bg-giphyIndigo'
    )

    return (
        <div
            role="alertdialog"
            aria-label={title}
            className={cn(
                'relative box-content rounded-[4px] bg-giphyBlack px-10 py-[50px] text-center text-giphyWhite',
                className
            )}
        >
            {stickerId && (
                <img
                    src={`https://media.giphy.com/media/${stickerId}/200.gif`}
                    alt=""
                    className="mx-auto mb-[10px] block"
                    style={{ height: 100, marginTop: -100 }}
                />
            )}
            <div className="mb-5 text-2xl font-black antialiased">{title}</div>
            {description && (
                <p className="max-w-[400px] leading-[1.3]">
                    {description} {fixDescription}
                </p>
            )}
            <div className="mx-auto mt-[30px] flex w-[300px] justify-center">
                <button type="button" onClick={onGoBack} className={buttonClassName}>
                    Go Back
                </button>
                {fixOutputType ? (
                    <button type="button" onClick={() => onChangeOutputType(fixOutputType)} className={buttonClassName}>
                        {fixTitle}
                    </button>
                ) : (
                    <a href={fixUrl} target="_blank" rel="noreferrer noopener" className={buttonClassName}>
                        {fixTitle}
                    </a>
                )}
            </div>
        </div>
    )
}

export default WelcomeErrorModal
